/** Tiempo de estancia por sitio: tabla fija por tipo y, si hay cupo, una estimación rápida de la IA. */
import { aiCoolingDown, askArgosAi } from "./aiClient";

const KIND_DEFAULTS: Record<string, number> = {
  museum: 90,
  gallery: 60,
  park: 45,
  plaza: 25,
  square: 25,
  monument: 30,
  castle: 75,
  viewpoint: 20,
  church: 25,
  library: 40,
  attraction: 50,
  ruins: 40,
  townhall: 15,
  cafe: 35,
  restaurant: 60,
  bar: 40,
  cinema: 120,
  bowling: 75,
  nightclub: 90,
  shop: 30,
  supermarket: 25,
  mountain: 80,
  bridge: 15,
  marketplace: 40,
};

/** Minutos típicos por tipo de sitio, sin IA. */
export function dwellFallbackMinutes(kind: string): number {
  return KIND_DEFAULTS[kind] ?? 40;
}

export function parseMinutes(text: string): number | null {
  // «1,5 horas» es hora y media, no «5 horas».
  const lower = text.toLowerCase().replace(/(\d),(\d)/g, "$1.$2");
  const day = lower.match(/(\d+(?:\.\d+)?)\s*d[ií]as?/);
  if (day) return Math.round(Number(day[1]) * 24 * 60);
  const andHalf = lower.match(/(?:(\d+)\s*)?horas?\s+y\s+media/);
  if (andHalf) return Number(andHalf[1] || 1) * 60 + 30;
  if (/\bmedia\s+hora/.test(lower)) return 30;
  const hm = lower.match(/(\d+(?:\.\d+)?)\s*h(?:oras?|ours?)?\b(?:\s*y)?\s*(\d+)?\s*m?/);
  if (hm) return Math.round(Number(hm[1]) * 60 + Number(hm[2] || 0));
  const onlyM = lower.match(/(\d+)\s*min/);
  if (onlyM) return Number(onlyM[1]);
  const bare = lower.match(/\b(\d{2,3})\b/);
  if (bare) {
    const n = Number(bare[1]);
    if (n >= 10 && n <= 240) return n;
  }
  return null;
}

const dwellCache = new Map<string, number>();
const recentAiCalls: number[] = [];
/** Como mucho unas pocas consultas a la IA por minuto: una ruta no debe gastar el cupo ni colgarse. */
const MAX_AI_CALLS_PER_MINUTE = 4;

function aiQuotaLeft(now: number): boolean {
  while (recentAiCalls.length && now - recentAiCalls[0] > 60_000) recentAiCalls.shift();
  return recentAiCalls.length < MAX_AI_CALLS_PER_MINUTE;
}

export async function askDwellMinutes(placeName: string, kind: string): Promise<number> {
  const fallback = KIND_DEFAULTS[kind] ?? 40;
  const key = `${placeName.trim().toLowerCase()}|${kind}`;
  const cached = dwellCache.get(key);
  if (cached != null) return cached;
  const now = Date.now();
  if (aiCoolingDown(now) || !aiQuotaLeft(now)) return fallback;
  recentAiCalls.push(now);
  const prompt = `Responde solo con un número de minutos (entre 15 y 180). ¿Cuánto tiempo se suele pasar visitando «${placeName}» (${kind})?`;
  const result = await askArgosAi(
    [
      { role: "system", content: "Responde solo con minutos enteros, sin texto extra." },
      { role: "user", content: prompt },
    ],
    { timeoutMs: 10000, temperature: 0.2 },
  );
  const parsed = result.ok ? parseMinutes(result.text) : null;
  const minutes = parsed != null ? Math.max(15, Math.min(180, parsed)) : fallback;
  if (parsed != null) dwellCache.set(key, minutes);
  return minutes;
}
